import { apiClient } from "./apiClient";
import { contactsService } from "./contactsService";

/**
 * A label groups contacts so campaigns can target them. Labels live on
 * atlas-api (`/api/ui/v1/labels`), not on the campaign service — the campaign
 * payload only references them by `{id, name}`.
 */
export interface LabelCreateInput {
  name: string;
  /** Free-text note on what the label is for. Optional. */
  labelDescription?: string;
  legalEntityId: string;
  /** Branch id, or `"0"` for a legal-entity-wide label (no branch selected). */
  branchId: string;
  contactIds: string[];
}

export interface LabelSummary {
  id: string;
  name: string;
  /** Free-text note on what the label is for, when the row carries one. */
  labelDescription?: string | null;
  legalEntityId?: string | null;
  branchId?: string | null;
  /** Contacts the label groups. Used to prefill the form when editing one. */
  contactIds?: string[] | null;
  /** Some responses nest the contacts instead of listing their ids. */
  contacts?: { id: string }[] | null;
  /** How many contacts the label groups, when the listing counts them for us. */
  contactCount?: number | null;
  /** When the label was created, as the listing reports it. */
  atlasCreatedAt?: string | null;
  /** When the label was last changed, as the listing reports it. */
  atlasModifiedAt?: string | null;
}

/** Contact ids of a label, whichever of the two shapes the response used. */
export const labelContactIds = (label: LabelSummary): string[] =>
  label.contactIds ?? label.contacts?.map((c) => c.id) ?? [];

/** The fields an update replaces — the label keeps its id and legal entity. */
export type LabelUpdateInput = Omit<LabelCreateInput, "legalEntityId">;

/** One reason a labelled contact cannot be messaged on the chosen channel. */
export interface LabelReachoutIssue {
  code: string;
  message: string | null;
}

/** A contact the reachout preview would skip, with why. */
export interface LabelReachoutBlockedContact {
  contact_id: string;
  name: string | null;
  legal_entity_id: string | null;
  issues: LabelReachoutIssue[];
}

/**
 * Dry-run of "can we message the people on these labels?" from
 * `POST /labels/reachout-preview`. Never publishes.
 */
export interface LabelReachoutPreview {
  channel_type: string;
  all_ready: boolean;
  total: number;
  ready: number;
  blocked: number;
  issue_counts: Record<string, number>;
  ready_contact_ids: string[];
  blocked_contacts: LabelReachoutBlockedContact[];
}

/** The scope a listing is narrowed to. */
export interface LabelScope {
  legalEntityId?: string | null;
  branchId?: string | null;
  /**
   * Several branches at once, sent as repeated `branchIds` params. Used when the
   * caller is scoped to a record rather than to the header — a contact's own
   * branches, say — and takes precedence over {@link branchId}.
   */
  branchIds?: string[] | null;
  /**
   * Keeps the header's branch out of the query when the caller's own scope
   * is "all branches" — without it the interceptor back-fills one and the
   * entity-wide list silently narrows to a single branch.
   */
  skipGlobalBranchId?: boolean;
  /**
   * Keep the header's legal entity/branch out entirely — used when the caller
   * asked for every entity ("All legal entities") rather than one from settings.
   */
  skipGlobalContext?: boolean;
  /**
   * Case-insensitive substring match on the label name (`?name=` or `?search=`).
   * Omitted to list every label in the scope.
   */
  name?: string | null;
}

/** One page of labels. Standalone so {@link listAll} can page over it. */
const listLabels = (page = 0, size = 200, scope?: LabelScope): Promise<LabelSummary[]> => {
  const branchIds = (scope?.branchIds ?? []).map((id) => String(id).trim()).filter(Boolean);
  const name = scope?.name?.trim();
  return apiClient
    .get("/labels", {
      params: {
        page,
        size,
        ...(scope?.legalEntityId ? { legalEntityId: scope.legalEntityId } : {}),
        ...(branchIds.length > 0 ? { branchIds } : scope?.branchId ? { branchId: scope.branchId } : {}),
        ...(scope?.skipGlobalBranchId && !scope.branchId ? { skipGlobalBranchId: true } : {}),
        ...(scope?.skipGlobalContext ? { skipGlobalContext: true } : {}),
        ...(name ? { name } : {}),
      },
      // `branchIds` has to repeat the bare key — `branchIds=a&branchIds=b` —
      // where axios would otherwise bracket it as `branchIds[]=a`.
      paramsSerializer: { indexes: null },
    })
    .then((r) => {
      // The endpoint has been seen returning a bare array and a paged envelope,
      // so both are accepted rather than assuming one.
      const data = r.data as LabelSummary[] | { content?: LabelSummary[]; items?: LabelSummary[] } | undefined;
      if (Array.isArray(data)) return data;
      return data?.content ?? data?.items ?? [];
    });
};

/** Rows per request when walking the whole list. */
const PAGE_SIZE = 200;
/**
 * Stops a paging bug — or a backend that ignores `page` — from looping forever.
 * At the page size above this is 4,000 labels, far past any real scope.
 */
const MAX_PAGES = 20;

type Raw = Record<string, unknown>;
const isRecord = (value: unknown): value is Raw => !!value && typeof value === "object" && !Array.isArray(value);

function asString(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function asNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(asString).filter((id): id is string => Boolean(id));
}

function asIssueCounts(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  const counts: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value)) {
    const n = asNumber(raw);
    if (n > 0) counts[key] = n;
  }
  return counts;
}

function asIssues(value: unknown): LabelReachoutIssue[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const code = asString(entry.code) ?? asString(entry.issue_code) ?? asString(entry.issueCode);
    if (!code) return [];
    return [
      {
        code,
        message: asString(entry.message) ?? asString(entry.detail) ?? null,
      },
    ];
  });
}

function asBlockedContacts(value: unknown): LabelReachoutBlockedContact[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!isRecord(entry)) return [];
    const contact_id =
      asString(entry.contact_id) ?? asString(entry.contactId) ?? asString(entry.id);
    if (!contact_id) return [];
    return [
      {
        contact_id,
        name: asString(entry.name) ?? asString(entry.display_name) ?? asString(entry.displayName),
        legal_entity_id: asString(entry.legal_entity_id) ?? asString(entry.legalEntityId),
        issues: asIssues(entry.issues),
      },
    ];
  });
}

function normalizeReachoutPreview(raw: unknown, fallbackChannel: string): LabelReachoutPreview {
  const source = isRecord(raw) ? raw : {};
  const ready_contact_ids = asStringList(
    source.ready_contact_ids ?? source.readyContactIds ?? source.ready_ids,
  );
  const blocked_contacts = asBlockedContacts(source.blocked_contacts ?? source.blockedContacts);
  const ready = source.ready != null ? asNumber(source.ready) : ready_contact_ids.length;
  const blocked = source.blocked != null ? asNumber(source.blocked) : blocked_contacts.length;
  const total = source.total != null ? asNumber(source.total) : ready + blocked;
  const allReadyRaw = source.all_ready ?? source.allReady;
  return {
    channel_type:
      asString(source.channel_type) ?? asString(source.channelType) ?? fallbackChannel,
    all_ready: typeof allReadyRaw === "boolean" ? allReadyRaw : blocked === 0 && total > 0,
    total,
    ready,
    blocked,
    issue_counts: asIssueCounts(source.issue_counts ?? source.issueCounts),
    ready_contact_ids,
    blocked_contacts,
  };
}

export const labelsService = {
  /**
   * Labels for a scope. Callers that own their own scope (the campaign form's
   * entity/branch picker) pass it explicitly and it wins over the header
   * selection; omitting both leaves the apiClient interceptor to inject the
   * header's legal entity and branch as before.
   */
  list: listLabels,

  /**
   * Every label in the scope, paged through until the endpoint runs out.
   *
   * Every label in the scope, paged through until the endpoint runs out.
   * Pass {@link LabelScope.name} to ask the server to match on name first.
   */
  listAll: async (scope?: LabelScope): Promise<LabelSummary[]> => {
    const all: LabelSummary[] = [];
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const batch = await listLabels(page, PAGE_SIZE, scope);
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
    }
    // A backend that ignores `page` would hand back the same rows each time.
    const seen = new Set<string>();
    return all.filter((label) => {
      const id = String(label.id ?? "");
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  },

  /**
   * Creates labels. The endpoint takes a batch, so the array is part of the
   * contract even when the UI only ever sends one.
   */
  create: (labels: LabelCreateInput[]): Promise<LabelSummary[]> =>
    apiClient.post("/labels", { labels }).then((r) => {
      const data = r.data as LabelSummary[] | { labels?: LabelSummary[] } | undefined;
      if (Array.isArray(data)) return data;
      return data?.labels ?? [];
    }),

  /**
   * Replaces a label's name and membership. Unlike {@link create} this takes a
   * single label, addressed by id in the path.
   */
  update: (id: string, label: LabelUpdateInput): Promise<LabelSummary | null> =>
    apiClient.patch(`/labels/${id}`, label).then((r) => (r.data as LabelSummary) ?? null),

  /**
   * Adds contacts to labels without dropping anyone already grouped. The patch
   * replaces membership, so existing ids are fetched first and unioned.
   */
  async addContacts(label: LabelSummary, contactIds: string[]): Promise<LabelSummary | null> {
    const extra = contactIds.map(String).filter(Boolean);
    if (extra.length === 0) return label;
    let existing: string[] = [];
    try {
      existing = await contactsService.listContactIdsByLabels([String(label.id)]);
    } catch {
      existing = labelContactIds(label);
    }
    const contactIdsMerged = [...new Set([...existing, ...extra])];
    return labelsService.update(label.id, {
      name: label.name,
      labelDescription: label.labelDescription ?? "",
      branchId: label.branchId && String(label.branchId).trim() ? String(label.branchId) : "0",
      contactIds: contactIdsMerged,
    });
  },

  /**
   * Drops contacts from a label, leaving the rest of the membership intact. The
   * same replace-semantics patch as {@link addContacts}, with the ids subtracted
   * rather than unioned.
   */
  async removeContacts(label: LabelSummary, contactIds: string[]): Promise<LabelSummary | null> {
    const drop = new Set(contactIds.map(String).filter(Boolean));
    if (drop.size === 0) return label;
    let existing: string[] = [];
    try {
      existing = await contactsService.listContactIdsByLabels([String(label.id)]);
    } catch {
      existing = labelContactIds(label);
    }
    const remaining = [...new Set(existing.map(String))].filter((id) => !drop.has(id));
    // Nothing on this label was picked - the patch would be a no-op round trip.
    if (remaining.length === existing.length) return label;
    return labelsService.update(label.id, {
      name: label.name,
      labelDescription: label.labelDescription ?? "",
      branchId: label.branchId && String(label.branchId).trim() ? String(label.branchId) : "0",
      contactIds: remaining,
    });
  },

  /**
   * Whether the contacts on these labels can be messaged on a channel.
   * Does not send anything — used to confirm before a campaign is created.
   */
  previewReachout: (
    labelIds: string[],
    channelType: string,
    propertyId?: string | null,
  ): Promise<LabelReachoutPreview> => {
    const ids = labelIds.map(String).filter(Boolean);
    const property = propertyId?.trim();
    return apiClient
      .post("/labels/reachout-preview", {
        label_ids: ids,
        channel_type: channelType,
        ...(property ? { property_id: property } : {}),
      })
      .then((r) => normalizeReachoutPreview(r.data, channelType));
  },
};
